import { z } from 'zod';

import { AssistantHandoffToolMetadataSchema } from './assistantHandoffTool.js';
import { VoiceCallTransferCallToolDestinationSchema } from './transferTool.js';
import { type BaseItem } from '../../common.js';
import { END_CALL_MESSAGE_MAX_LENGTH } from '../assistantDefinition/constants.js';

export * from './assistantHandoffTool.js';
export * from './transferTool.js';

export enum AssistantToolType {
  authenticateInboundPatient = 'authenticateInboundPatient',
  authenticateKnownPatient = 'authenticateKnownPatient',
  bookSelectedSlot = 'bookSelectedSlot',
  createPatient = 'createPatient',
  getAppointments = 'getAppointments',
  /**
   * @deprecated - use getEncounters instead
   */
  getEncounterDetails = 'getEncounterDetails',
  getEncounters = 'getEncounters',
  getInsurance = 'getInsurance',
  getOrders = 'getOrders',
  getPatient = 'getPatient',
  getPracticeDetails = 'getPracticeDetails',
  getTimeDifference = 'getTimeDifference',
  lookupPatient = 'lookupPatient',
  searchForOpenSlots = 'searchForOpenSlots',
  sendInboundAuthCode = 'sendInboundAuthCode',
  sendSms = 'sendSms',
  traverseDecisionTree = 'traverseDecisionTree',
  EndCall = 'EndCall',
  TransferCall = 'TransferCall',
  NotableTransferCall = 'NotableTransferCall',
  AssistantHandoff = 'AssistantHandoff',
}

// With no metadata requirement
export const GenericToolTypes = [
  AssistantToolType.authenticateInboundPatient,
  AssistantToolType.authenticateKnownPatient,
  AssistantToolType.bookSelectedSlot,
  AssistantToolType.createPatient,
  AssistantToolType.getAppointments,
  AssistantToolType.getEncounters,
  AssistantToolType.getInsurance,
  AssistantToolType.getOrders,
  AssistantToolType.getPatient,
  AssistantToolType.getPracticeDetails,
  AssistantToolType.getTimeDifference,
  AssistantToolType.lookupPatient,
  AssistantToolType.searchForOpenSlots,
  AssistantToolType.sendInboundAuthCode,
  AssistantToolType.sendSms,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  AssistantToolType.getEncounterDetails,
] as const;

// Can be configured in the tool tab than a special UI
export const ToolTabConfigurableToolTypes = [
  ...GenericToolTypes,
  AssistantToolType.traverseDecisionTree,
] as const;
export type ToolTabConfigurableToolType = (typeof ToolTabConfigurableToolTypes)[number];

export const BaseGenericToolDefinitionSchema = z.object({
  id: z.uuid().optional(),
  type: z.enum(AssistantToolType),
  name: z.string(),
  organizationId: z.uuid(),
  description: z.string().nullable(),
  summary: z.string().nullable(),
  parameters: z.unknown(),
  startMessage: z.string().nullable(),
  failedMessage: z.string().nullable(),
  delayedMessage: z.string().nullable(),
  delayThresholdMilliseconds: z.number().nullable(),
  completeMessage: z.string().nullable(),
  timeoutSeconds: z.number().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  endCallAfterCompleteMessageSpoken: z.boolean().nullable(),
  endCallAfterFailedMessageSpoken: z.boolean().nullable(),
});

export const NotableDefinedToolDefinitionUpsertValuesSchema = BaseGenericToolDefinitionSchema.pick({
  id: true,
  type: true,
  name: true,
  organizationId: true,
  summary: true,
  startMessage: true,
  failedMessage: true,
  delayedMessage: true,
  completeMessage: true,
  delayThresholdMilliseconds: true,
  metadata: true,
  endCallAfterCompleteMessageSpoken: true,
  endCallAfterFailedMessageSpoken: true,
});

const BaseGenericOnlyToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.enum(GenericToolTypes),
});
export type BaseGenericOnlyToolDefinition = z.infer<typeof BaseGenericOnlyToolDefinitionSchema>;

export const GenericToolDefinitionUpsertValuesSchema = BaseGenericOnlyToolDefinitionSchema.pick({
  id: true,
  type: true,
  name: true,
  organizationId: true,
  summary: true,
  startMessage: true,
  failedMessage: true,
  delayedMessage: true,
  delayThresholdMilliseconds: true,
  completeMessage: true,
  metadata: true,
  endCallAfterCompleteMessageSpoken: true,
  endCallAfterFailedMessageSpoken: true,
});

const BaseDecisionTreeToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.traverseDecisionTree),
  metadata: z.object({
    decisionTreeId: z.uuid(),
  }),
});
export type BaseDecisionTreeToolDefinition = z.infer<typeof BaseDecisionTreeToolDefinitionSchema>;
export const DecisionTreeToolDefinitionUpsertValuesSchema =
  BaseDecisionTreeToolDefinitionSchema.pick({
    id: true,
    type: true,
    name: true,
    organizationId: true,
    summary: true,
    metadata: true,
    startMessage: true,
    failedMessage: true,
    delayedMessage: true,
    delayThresholdMilliseconds: true,
    completeMessage: true,
    endCallAfterCompleteMessageSpoken: true,
    endCallAfterFailedMessageSpoken: true,
  });
export type DecisionTreeToolDefinitionUpsertValues = z.infer<
  typeof DecisionTreeToolDefinitionUpsertValuesSchema
>;

const TransferCallToolMetadataSchema = z.object({
  destinations: z.array(VoiceCallTransferCallToolDestinationSchema),
});

// Upsert (write) path requires at least one destination, empty destinations are non-functional at runtime.
const TransferCallToolUpsertMetadataSchema = z.object({
  destinations: z.array(VoiceCallTransferCallToolDestinationSchema).min(1),
});

/** Read-only: there is deliberately no `TransferCallToolDefinitionUpsertValuesSchema`. */
export const BaseTransferCallToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.TransferCall),
  metadata: TransferCallToolMetadataSchema,
});
export type BaseTransferCallToolDefinition = z.infer<typeof BaseTransferCallToolDefinitionSchema>;

export const BaseNotableTransferCallToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.NotableTransferCall),
  metadata: TransferCallToolMetadataSchema,
});
export const NotableTransferCallToolDefinitionUpsertValuesSchema =
  BaseNotableTransferCallToolDefinitionSchema.pick({
    id: true,
    type: true,
    name: true,
    description: true,
    organizationId: true,
    summary: true,
  }).extend({
    metadata: TransferCallToolUpsertMetadataSchema,
  });
export type BaseNotableTransferCallToolDefinition = z.infer<
  typeof BaseNotableTransferCallToolDefinitionSchema
>;
export type NotableTransferCallToolDefinitionUpsertValues = z.infer<
  typeof NotableTransferCallToolDefinitionUpsertValuesSchema
>;

export const BaseAssistantHandoffToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.AssistantHandoff),
  metadata: AssistantHandoffToolMetadataSchema,
});
export const AssistantHandoffToolDefinitionUpsertValuesSchema =
  BaseAssistantHandoffToolDefinitionSchema.pick({
    id: true,
    type: true,
    name: true,
    organizationId: true,
    summary: true,
    metadata: true,
    startMessage: true,
    failedMessage: true,
    delayedMessage: true,
    delayThresholdMilliseconds: true,
    completeMessage: true,
    endCallAfterCompleteMessageSpoken: true,
    endCallAfterFailedMessageSpoken: true,
  });
export type BaseAssistantHandoffToolDefinition = z.infer<
  typeof BaseAssistantHandoffToolDefinitionSchema
>;
export type AssistantHandoffToolDefinitionUpsertValues = z.infer<
  typeof AssistantHandoffToolDefinitionUpsertValuesSchema
>;

export const BaseEndCallToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.EndCall),
  startMessage: z.string().max(END_CALL_MESSAGE_MAX_LENGTH).nullable(),
});
export const EndCallToolDefinitionUpsertValuesSchema = BaseEndCallToolDefinitionSchema.pick({
  id: true,
  type: true,
  name: true,
  organizationId: true,
  summary: true,
  startMessage: true,
});
export type BaseEndCallToolDefinition = z.infer<typeof BaseEndCallToolDefinitionSchema>;
export type EndCallToolDefinitionUpsertValues = z.infer<
  typeof EndCallToolDefinitionUpsertValuesSchema
>;

export const ToolDefinitionUpsertValuesSchema = z.discriminatedUnion('type', [
  GenericToolDefinitionUpsertValuesSchema,
  NotableTransferCallToolDefinitionUpsertValuesSchema,
  AssistantHandoffToolDefinitionUpsertValuesSchema,
  EndCallToolDefinitionUpsertValuesSchema,
  DecisionTreeToolDefinitionUpsertValuesSchema,
]);

export type ToolDefinitionUpsertValues = z.infer<typeof ToolDefinitionUpsertValuesSchema>;

export const ToolTabConfigurableToolDefinitionUpsertValuesSchema = z.discriminatedUnion('type', [
  GenericToolDefinitionUpsertValuesSchema,
  DecisionTreeToolDefinitionUpsertValuesSchema,
]);

export type ToolTabConfigurableToolDefinitionUpsertValues = z.infer<
  typeof ToolTabConfigurableToolDefinitionUpsertValuesSchema
>;

export const ToolTabConfigurableToolDefinitionCreateSchema = z.union([
  GenericToolDefinitionUpsertValuesSchema.omit({
    id: true,
  }),
  DecisionTreeToolDefinitionUpsertValuesSchema.omit({
    id: true,
  }),
]);
export type ToolTabConfigurableToolDefinitionCreateValues = z.infer<
  typeof ToolTabConfigurableToolDefinitionCreateSchema
>;
export const ToolTabConfigurableToolDefinitionUpdateSchema = z.union([
  GenericToolDefinitionUpsertValuesSchema.omit({
    id: true,
    name: true,
    organizationId: true,
  }),
  DecisionTreeToolDefinitionUpsertValuesSchema.omit({
    id: true,
    name: true,
    organizationId: true,
  }),
]);
export type ToolTabConfigurableToolDefinitionUpdateValues = z.infer<
  typeof ToolTabConfigurableToolDefinitionUpdateSchema
>;

export const BaseToolDefinitionSchema = z.discriminatedUnion('type', [
  BaseTransferCallToolDefinitionSchema,
  BaseNotableTransferCallToolDefinitionSchema,
  BaseAssistantHandoffToolDefinitionSchema,
  BaseEndCallToolDefinitionSchema,
  BaseGenericOnlyToolDefinitionSchema,
]);
export type BaseToolDefinition = z.infer<typeof BaseToolDefinitionSchema>;
export type ToolDefinition = BaseItem & BaseToolDefinition;

export type BaseToolTabConfigurableToolDefinition =
  | BaseGenericOnlyToolDefinition
  | BaseDecisionTreeToolDefinition;
export type ToolTabConfigurableToolDefinition = BaseItem & BaseToolTabConfigurableToolDefinition;
export type ToolTabConfigurableToolDefinitionWithUsage = ToolTabConfigurableToolDefinition & {
  /** Defined when queried with includeUsage: true */
  usedByAssistants?: string[];
};

export const ToolTabConfigurableToolDefinitionFindAllOptionsSchema = z.object({
  organizationIds: z.array(z.uuid()).optional(),
  ids: z.array(z.uuid()).optional(),
  assistantDefinitionId: z.uuid().optional(),
  types: z.array(z.enum([...ToolTabConfigurableToolTypes])).optional(),
  name: z.string().optional(),
  includeUsage: z.boolean().optional(),
});
export type ToolTabConfigurableToolDefinitionFindAllOptions = z.infer<
  typeof ToolTabConfigurableToolDefinitionFindAllOptionsSchema
>;

export interface ToolTabConfigurableToolDefinitionFindOneOptions {
  includeUsage?: boolean;
}

export function isToolTabConfigurableTools<
  T extends {
    type: AssistantToolType;
  },
>(toolDefinition: T): toolDefinition is T & { type: ToolTabConfigurableToolType } {
  return ToolTabConfigurableToolTypes.some((type) => type === toolDefinition.type);
}

export function isDecisionTreeToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseDecisionTreeToolDefinition {
  return toolDefinition.type === AssistantToolType.traverseDecisionTree;
}

export function isTransferCallToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseTransferCallToolDefinition {
  return toolDefinition.type === AssistantToolType.TransferCall;
}

export function isNotableTransferCallToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseNotableTransferCallToolDefinition {
  return toolDefinition.type === AssistantToolType.NotableTransferCall;
}

export function isAssistantHandoffToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseAssistantHandoffToolDefinition {
  return toolDefinition.type === AssistantToolType.AssistantHandoff;
}

export function isEndCallToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseEndCallToolDefinition {
  return toolDefinition.type === AssistantToolType.EndCall;
}
